import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Trash2, Archive, ShieldAlert, WifiOff, DatabaseZap } from "lucide-react";

/**
 * Data Management (Reset & Delete) — shared by Owner Settings and Admin Records.
 * Server enforces tenancy; this card only ever operates on ONE haat at a time.
 * Destructive actions are blocked while offline (never queued).
 */
export default function DataManagementCard({ haats }) {
  const [haatId, setHaatId] = useState("");
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState("archive");
  const [open, setOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  useEffect(() => {
    if (!haatId && haats?.length) setHaatId(haats[0].haat_id);
  }, [haats, haatId]);

  const loadSummary = useCallback(() => {
    if (!haatId) return;
    api.get("/data/summary", { params: { haat_id: haatId } })
      .then((r) => setSummary(r.data))
      .catch((e) => { setSummary(null); toast.error(e?.response?.data?.detail || "Failed to load data summary"); });
  }, [haatId]);
  useEffect(() => { loadSummary(); setSelected(new Set()); }, [loadSummary]);

  const toggle = (key) => setSelected((s) => {
    const c = new Set(s);
    if (c.has(key)) c.delete(key); else c.add(key);
    return c;
  });

  const cats = summary?.categories || [];
  const chosen = cats.filter((c) => selected.has(c.key));
  const affected = chosen.reduce((n, c) => n + (mode === "permanent" ? c.live + c.archived : c.live), 0);

  const openConfirm = () => {
    if (!online) { toast.error("Internet connection is required for destructive operations."); return; }
    if (chosen.length === 0) { toast.error("Select at least one data category"); return; }
    setConfirmName(""); setAck(false); setOpen(true);
  };

  const run = async () => {
    if (!online) { toast.error("Internet connection is required for destructive operations."); return; }
    setBusy(true);
    try {
      const r = await api.post("/data/reset", {
        haat_id: haatId,
        categories: [...selected],
        mode,
        confirm_name: confirmName,
        confirm_permanent: ack,
      });
      toast.success(`${mode === "permanent" ? "Permanently deleted" : "Archived"} ${r.data.total} record(s)`);
      setOpen(false); setSelected(new Set());
      loadSummary();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "confirm_name_mismatch" ? "Typed name does not match the Pashu Haat name"
        : d === "permanent_ack_required" ? "Tick the permanent-delete acknowledgement"
        : d || "Operation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="am-card p-6 space-y-4 mt-6 border-destructive/40" data-testid="dm-card">
      <div className="flex items-center gap-2">
        <DatabaseZap className="w-5 h-5 text-destructive" />
        <h2 className="font-display text-xl font-bold">Data Management — Reset &amp; Delete</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Archive (restorable from Admin Records) or permanently delete this Pashu Haat's market data.
        User accounts, haat configuration, pricing and subscription are never touched.
        Reports update automatically since they are computed from the data below.
      </p>

      {!online && (
        <div className="rounded-xl bg-amber-100 text-amber-900 text-sm font-semibold px-4 py-3 flex items-center gap-2" data-testid="dm-offline-note">
          <WifiOff className="w-4 h-4 shrink-0" /> Internet connection is required for reset / delete. These actions are never queued offline.
        </div>
      )}

      {haats?.length > 1 && (
        <div className="max-w-xs">
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Pashu Haat</label>
          <Select value={haatId} onValueChange={setHaatId}>
            <SelectTrigger className="am-input mt-1" data-testid="dm-haat-select"><SelectValue placeholder="Select haat" /></SelectTrigger>
            <SelectContent>
              {haats.map((h) => <SelectItem key={h.haat_id} value={h.haat_id}>{h.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {summary && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {cats.map((c) => (
              <label key={c.key} className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer transition-colors ${selected.has(c.key) ? "border-destructive bg-destructive/5" : "border-border hover:bg-muted"}`}>
                <input type="checkbox" data-testid={`dm-cat-${c.key}`} checked={selected.has(c.key)} onChange={() => toggle(c.key)} className="mt-0.5 h-4 w-4 accent-destructive cursor-pointer" />
                <span className="min-w-0">
                  <span className="block text-sm font-bold leading-tight">{c.label}</span>
                  <span className="block text-xs text-muted-foreground mt-0.5" data-testid={`dm-count-${c.key}`}>
                    {c.live} live{c.archived > 0 ? ` · ${c.archived} archived` : ""}
                  </span>
                </span>
              </label>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <input type="radio" name="dm-mode" data-testid="dm-mode-archive" checked={mode === "archive"} onChange={() => setMode("archive")} className="h-4 w-4 accent-primary" />
              <Archive className="w-4 h-4 text-primary" /> Reset (archive — restorable)
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <input type="radio" name="dm-mode" data-testid="dm-mode-permanent" checked={mode === "permanent"} onChange={() => setMode("permanent")} className="h-4 w-4 accent-destructive" />
              <Trash2 className="w-4 h-4 text-destructive" /> Permanent delete (cannot be undone)
            </label>
          </div>

          <Button
            data-testid="dm-open-btn"
            variant="destructive"
            className="rounded-full"
            disabled={!online || chosen.length === 0}
            onClick={openConfirm}
          >
            <ShieldAlert className="w-4 h-4 mr-2" />
            {mode === "permanent" ? "Permanently Delete Selected Data…" : "Reset Selected Data…"}
          </Button>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="w-5 h-5" /> Confirm {mode === "permanent" ? "PERMANENT DELETE" : "data reset"}
            </DialogTitle>
            <DialogDescription>
              Pashu Haat: <b className="text-foreground">{summary?.haat_name}</b>
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border border-border divide-y divide-border text-sm" data-testid="dm-dialog-summary">
            {chosen.map((c) => (
              <div key={c.key} className="flex justify-between px-3 py-2">
                <span>{c.label}</span>
                <span className="font-bold">{mode === "permanent" ? c.live + c.archived : c.live} records</span>
              </div>
            ))}
            <div className="flex justify-between px-3 py-2 bg-muted font-bold">
              <span>Total affected</span><span data-testid="dm-dialog-total">{affected}</span>
            </div>
          </div>

          <div className="rounded-xl bg-amber-100 text-amber-900 text-xs font-semibold px-3 py-2">
            Recommended: download a backup / export before large operations.
            {mode === "archive"
              ? " Archived data can be restored by the Super Admin from Records."
              : " Permanent deletion also removes previously archived rows and CANNOT be undone."}
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
              Type the Pashu Haat name to confirm: <span className="text-destructive">{summary?.haat_name}</span>
            </label>
            <Input data-testid="dm-confirm-name" className="am-input mt-1" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={summary?.haat_name} />
          </div>

          {mode === "permanent" && (
            <label className="flex items-start gap-2 text-sm font-semibold cursor-pointer">
              <input type="checkbox" data-testid="dm-permanent-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4 accent-destructive" />
              <span>I understand this permanently deletes the selected data and cannot be undone.</span>
            </label>
          )}

          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} data-testid="dm-cancel-btn">Cancel</Button>
            <Button
              variant="destructive"
              className="rounded-full"
              data-testid="dm-confirm-btn"
              disabled={busy || confirmName.trim() !== (summary?.haat_name || "").trim() || (mode === "permanent" && !ack)}
              onClick={run}
            >
              {busy ? "Working…" : mode === "permanent" ? "Permanently Delete" : "Reset Data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
